import { LogManager } from './Logger';

const logger = LogManager.getLogger('speakerFuzzyMatch');

export interface SpeakerMatchCandidate {
  name: string;
  title?: string;
}

export interface SpeakerMatchResult {
  score: number;
  nameScore: number;
  titleScore: number | null;
}

export const MATCH_THRESHOLD_AUTO = 0.85;
export const MATCH_THRESHOLD_REVIEW = 0.6;

/**
 * Strip Unicode diacritics and lowercase.
 * "Källenius" → "kallenius", "García" → "garcia"
 */
function normalize(s: string): string {
  return (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Levenshtein edit distance between two strings.
 */
function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () =>
    Array(n + 1).fill(0),
  );
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

/**
 * Similarity ratio: 1.0 = identical, 0.0 = completely different.
 */
function levenshteinRatio(a: string, b: string): number {
  if (a === b) return 1;
  if (!a || !b) return 0;
  return 1 - levenshtein(a, b) / Math.max(a.length, b.length);
}

const TOKEN_MATCH_THRESHOLD = 0.75;

const CONTAINMENT_SCORE = 0.9;

/**
 * Containment where the contained side is a lone token ("Scott" in "Scott
 * Rutherford"). Deliberately lands between MATCH_THRESHOLD_REVIEW and
 * MATCH_THRESHOLD_AUTO: worth offering, never enough to decide on. A single
 * token cannot distinguish between the people who share it, so it must not
 * outscore evidence that actually identifies someone.
 */
const PARTIAL_CONTAINMENT_SCORE = 0.75;

/**
 * Fuzzy score between two free-text fields (name or title).
 * Handles: exact match, substring containment, token-level Levenshtein.
 */
function tokenFuzzyScore(a: string, b: string): number {
  const normA = normalize(a);
  const normB = normalize(b);
  if (!normA || !normB) return 0;
  if (normA === normB) return 1.0;

  const tokA = normA.split(/\s+/);
  const tokB = normB.split(/\s+/);

  if (normA.includes(normB) || normB.includes(normA)) {
    const fewestTokens = Math.min(tokA.length, tokB.length);
    const mostTokens = Math.max(tokA.length, tokB.length);
    return fewestTokens === 1 && mostTokens > 1
      ? PARTIAL_CONTAINMENT_SCORE
      : CONTAINMENT_SCORE;
  }

  const [shorter, longer] =
    tokA.length <= tokB.length ? [tokA, tokB] : [tokB, tokA];

  let matched = 0;
  const used = new Set<number>();
  // Greedy assignment: works well for short token lists (names are typically 2-3 tokens)
  for (const tok of shorter) {
    let bestIdx = -1;
    let bestRatio = 0;
    for (let i = 0; i < longer.length; i++) {
      if (used.has(i)) continue;
      const r = levenshteinRatio(tok, longer[i]);
      if (r > bestRatio) {
        bestRatio = r;
        bestIdx = i;
      }
    }
    if (bestRatio >= TOKEN_MATCH_THRESHOLD && bestIdx >= 0) {
      matched++;
      used.add(bestIdx);
    }
  }
  return matched / Math.max(tokA.length, tokB.length);
}

const NAME_WEIGHT = 0.7;
const TITLE_WEIGHT = 0.3;

/**
 * Compute a match score between an AI-identified speaker and an existing speaker.
 *
 * Uses structured name + title fields (not raw concatenated strings).
 * Returns a score from 0.0 to 1.0 plus component scores for transparency.
 *
 * Thresholds (exported as constants):
 *   >= MATCH_THRESHOLD_AUTO (0.85)   → high-confidence auto-match
 *   >= MATCH_THRESHOLD_REVIEW (0.6)  → flag for user review
 *   < 0.6                            → no match
 */
export function speakerMatchScore(
  identified: SpeakerMatchCandidate,
  existing: SpeakerMatchCandidate,
): SpeakerMatchResult {
  const nameScore = tokenFuzzyScore(identified.name, existing.name);

  const idTitle = normalize(identified.title || '');
  const exTitle = normalize(existing.title || '');
  const hasTitle = idTitle.length > 0 && exTitle.length > 0;
  const titleScore = hasTitle
    ? tokenFuzzyScore(identified.title || '', existing.title || '')
    : null;

  const total =
    titleScore !== null
      ? nameScore * NAME_WEIGHT + titleScore * TITLE_WEIGHT
      : nameScore;

  return {
    score: Math.round(total * 100) / 100,
    nameScore: Math.round(nameScore * 100) / 100,
    titleScore: titleScore !== null ? Math.round(titleScore * 100) / 100 : null,
  };
}

/**
 * Order two results best-first: higher score wins, and on a tie the one whose
 * title could be compared at all wins.
 *
 * A missing title drops out of the average rather than counting against, so a
 * record holding no title scores exactly as high as one that states the same
 * title we identified. Without this, that tie fell to DatoCMS return order and
 * the record carrying no title could win.
 *
 * Note this asks only whether both sides had a title, not whether the two
 * agreed — a title that disagrees drags the score down on its own, so it
 * rarely reaches a tie in the first place.
 */
function compareMatches(a: SpeakerMatchResult, b: SpeakerMatchResult): number {
  if (a.score !== b.score) return b.score - a.score;
  const hasTitleEvidence = (r: SpeakerMatchResult) =>
    r.titleScore !== null ? 1 : 0;
  return hasTitleEvidence(b) - hasTitleEvidence(a);
}

/**
 * Find the best matching existing speaker for an AI-identified speaker.
 *
 * Returns the best match above `minThreshold` (defaults to MATCH_THRESHOLD_REVIEW),
 * or null if no match meets the threshold. Equally-ranked candidates keep the
 * first encountered.
 */
export function findBestSpeakerMatch<T extends SpeakerMatchCandidate>(
  identified: SpeakerMatchCandidate,
  existingSpeakers: T[],
  minThreshold: number = MATCH_THRESHOLD_REVIEW,
): { match: T; result: SpeakerMatchResult } | null {
  let best: { match: T; result: SpeakerMatchResult } | null = null;

  for (const existing of existingSpeakers) {
    const result = speakerMatchScore(identified, existing);
    if (result.score >= minThreshold) {
      if (!best || compareMatches(result, best.result) < 0) {
        best = { match: existing, result };
      }
    }
  }

  if (best) {
    logger.debug(
      `Fuzzy match: "${identified.name}" → "${best.match.name}" (score=${best.result.score}, name=${best.result.nameScore}, title=${best.result.titleScore})`,
    );
  }

  return best;
}

/**
 * Rank all existing speakers by match score against an identified speaker.
 * Returns only those above `minThreshold`, sorted best-first.
 *
 * For building a suggestion list. To *pick* a match use `findBestSpeakerMatch`
 * — taking `[0]` from here at the review threshold treats a "show the user
 * this" score as a decision.
 */
export function rankSpeakerMatches<T extends SpeakerMatchCandidate>(
  identified: SpeakerMatchCandidate,
  existingSpeakers: T[],
  minThreshold: number = MATCH_THRESHOLD_REVIEW,
): Array<{ match: T; result: SpeakerMatchResult }> {
  return existingSpeakers
    .map(existing => ({
      match: existing,
      result: speakerMatchScore(identified, existing),
    }))
    .filter(({ result }) => result.score >= minThreshold)
    .sort((a, b) => compareMatches(a.result, b.result));
}
